// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
// The axios configuration can be changed according to the project, just change the file, other files can be left unchanged

import type {
  AxiosTransform,
  CreateAxiosOptions,
  RequestOptions,
  Result,
} from '@mp/http-axios'
import {
  AxiosRetry,
  ContentTypeEnum,
  RequestEnum,
  ResultEnum,
  VAxios,
  formatRequestDate,
  joinTimestamp,
  setObjToUrlParams,
} from '@mp/http-axios'
import { isString, merge } from '@utopia-utils/core'
import type { AxiosInstance, AxiosResponse } from 'axios'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ExcludeUrls, generateBodySignInterceptor, getPublicKey, legacyLogout } from '@mp/utils'

import { messageErrorFn } from './defineConfig'

// import { getToken } from '@/utils/auth'
// import { useErrorLogStoreWithOut } from '@/store/modules/errorLog'
// import { useI18n } from '@/hooks/web/useI18n'
// import { useUserStoreWithOut } from '@/store/modules/user'

import { checkStatus } from './checkStatus'
import { msgMap } from './msgMap'
import { getDeviceCode } from '../../../utils/src'

import 'element-plus/es/components/message/style/css'
import 'element-plus/es/components/message-box/style/css'
import { ZF_NO } from '@mp/constant'

// #region 显示登录过期弹窗 start

// 在当前文件中临时扩展 window 对象
declare global {
  interface Window {
    __IS_SHOW_LOGIN_EXPIRE_MODAL__: boolean
    deviceId?: string
  }
}

/** 请求超时时间 */
const TIMEOUT = 60 * 1000

const initTime = Date.now()
const showLoginExpireModal = (() => {
  window.__IS_SHOW_LOGIN_EXPIRE_MODAL__ = false
  return () => {
    const WhiteList = ['/order-management/audit', '/witness-payment']
    if (window.location.hash === '#/' || window.location.hash === '#/new' || WhiteList.some(i => window.location.pathname.includes(i)))
      return

    // 避免在页面刚刚加载时，就显示登录过期弹窗。这时候直接跳转登录页
    if (Date.now() - initTime < 3000) {
      legacyLogout()
      return
    }

    if (window.__IS_SHOW_LOGIN_EXPIRE_MODAL__ || window.parent?.__IS_SHOW_LOGIN_EXPIRE_MODAL__)
      // 同时判断主应用和子应用的弹窗状态，避免重复弹窗
      return
    window.__IS_SHOW_LOGIN_EXPIRE_MODAL__ = true
    ElMessageBox.confirm(
      '登录已过期，请重新登录',
      '登录过期',
      {
        confirmButtonText: '重新登录',
        type: 'warning',
        showCancelButton: false,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
      },
    ).then(() => {
      window.__IS_SHOW_LOGIN_EXPIRE_MODAL__ = false
      legacyLogout()
    })
  }
})()
// #endregion 显示登录过期弹窗 end

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理响应数据。如果数据不是预期格式，可直接抛出错误
   */
  transformResponseHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    const { isTransformResponse, isReturnNativeResponse } = options
    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse)
      return res

    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse)
      return res.data

    // 错误的时候返回

    const { data } = res
    if (!data) {
      // return '[HTTP] Request has no return value';
      throw new Error(msgMap.apiRequestFailed)
    }
    // if (typeof data === 'string' && data.startsWith('http')) {
    //   // 特殊场景，后端直接返回字符串
    //   return data
    // }

    // 如果 res 是文件流，直接 return
    if (res.data instanceof Blob)
      return res.data
    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    // const { code, result, message } = data
    const { resultCode, errorCodeDes, errorCode } = data

    if (errorCodeDes?.includes('应用或者证书编号错误'))
      sessionStorage.removeItem('publicKeyRes')

    // 这里逻辑可以根据项目进行修改
    const hasSuccess = typeof data === 'object' && Reflect.has(data, 'resultCode') && resultCode === ResultEnum.SUCCESS
    if (hasSuccess) {
      // let successMsg = message

      // if (successMsg === null || successMsg === undefined || isEmpty(successMsg))
      //   successMsg = msgMap.operationSuccess

      // if (options.successMessageMode === 'modal')
      //   Modal.success({ title: msgMap.successTip, content: successMsg })

      // else if (options.successMessageMode === 'message')
      //   ElMessage.success(successMsg)

      return data
    }

    // 在此处根据自己项目的实际情况对不同的code执行不同的操作
    // 如果不希望中断当前请求，请return数据，否则直接抛出异常即可
    let errorMsg = ''
    switch (errorCode) {
      case ResultEnum.TIMEOUT: {
        errorMsg = msgMap.timeoutMessage
        // const userStore = useUserStoreWithOut()
        // userStore.setToken(undefined)
        // userStore.logout(true)
        break
      }
      case 'LOGIN_EXPIRE':
      case ResultEnum.LOGIN_EXPIRED: {
        errorMsg = errorCodeDes
        showLoginExpireModal()
        return data
      }
      default:
        if (errorCodeDes)
          errorMsg = errorCodeDes
    }

    // errorMessageMode 默认是 message
    // errorMessageMode='none' 一般是调用时明确表示不希望自动弹出错误提示
    // if (options.errorMessageMode === 'modal')
    //   Modal.error({ title: msgMap.errorTip, content: errorMsg })
    // else
    if (options.errorMessageMode === 'message')
      ElMessage.error(errorMsg)

    return data
    // throw new Error(errorMsg || msgMap.apiRequestFailed)
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options

    // 如果响应类型为 blob，且超时时间未单独设置，则设置为 10 分钟
    const { timeout, responseType } = config
    if (responseType === 'blob' && !timeout)
      config.timeout = 1000 * 60 * 10

    if (joinPrefix)
      config.url = `${urlPrefix}${config.url}`

    if (apiUrl && isString(apiUrl))
      config.url = `${apiUrl}${config.url}`

    const params = config.params || {}
    const data = config.data || false

    formatDate && data && !isString(data) && formatRequestDate(data)
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false))
        // config.params = Object.assign(params || {})
      }
      else {
        // 兼容restful风格
        config.url = `${config.url + params}${joinTimestamp(joinTime, true)}`
        config.params = undefined
      }
    }
    else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params)
        if (
          Reflect.has(config, 'data')
          && config.data
          && (Object.keys(config.data).length > 0 || config.data instanceof FormData)
        ) {
          config.data = data
          config.params = params
        }
        else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data),
          )
        }
      }
      else {
        // 兼容restful风格
        config.url = config.url + params
        config.params = undefined
      }
    }
    return config
  },

  /**
   * @description: 请求拦截器处理
   */
  // @ts-ignore
  requestInterceptors: async (config) => {
    // 异步获取deviceId，更新window对象，供后续请求使用
    if (!window.deviceId) {
      const deviceCode = await getDeviceCode()
      window.deviceId = deviceCode
    }
    config.headers.deviceCode = window.deviceId
    return config
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    return res
  },

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: (axiosInstance: AxiosInstance, error: any) => {
    const { response, code, message, config } = error || {}
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none'
    const msg: string = response?.data?.error?.message ?? ''
    const err: string = error?.toString?.() ?? ''
    let errMessage = ''

    if (axios.isCancel(error))
      return Promise.reject(error)

    try {
      if (code === 'ECONNABORTED' && message.includes('timeout'))
        errMessage = msgMap.apiTimeoutMessage

      if (err?.includes('Network Error'))
        errMessage = msgMap.networkExceptionMsg

      if (errMessage) {
        // if (errorMessageMode === 'modal')
        //   Modal.error({ title: msgMap.errorTip, content: errMessage })
        // else
        if (errorMessageMode === 'message')
          messageErrorFn?.(errMessage)

        return Promise.reject(error)
      }
    }
    catch (error) {
      throw new Error(error as unknown as string)
    }

    if (error?.response?.status === 500)
      sessionStorage.removeItem('publicKeyRes')

    checkStatus(error?.response?.status, msg, errorMessageMode)

    // 添加自动重试机制 保险起见 只针对GET请求
    const retryRequest = new AxiosRetry()
    const { isOpenRetry } = config?.requestOptions?.retryRequest || {}
    config?.method?.toUpperCase() === RequestEnum.GET
      && isOpenRetry
      && retryRequest.retry(axiosInstance, error)
    return Promise.reject(error)
  },
}

export function createAxios(opt?: Partial<CreateAxiosOptions>) {
  const axiosInstance = new VAxios(
    // 深度合并
    merge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: '',
        timeout: TIMEOUT,
        // 基础接口地址
        // baseURL: globSetting.apiUrl,

        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: { ...transform },
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          joinPrefix: true,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          //  是否加入时间戳
          joinTime: true,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
          retryRequest: {
            isOpenRetry: false,
            count: 3,
            waitTime: 100,
          },
        },
      },
      opt || {},
    ),
  )

  axiosInstance.getAxios().interceptors.request.use(
    async (config: any) => {
      const isSignHeader = ExcludeUrls?.some(u => config?.url?.includes(u))

      if (!isSignHeader) {
        // 加密拦截器先执行时会写入 _publicKeyRes，此处复用同一份公钥，避免过期边界两次 getPublicKey 不一致
        const publicKeyRes = config._publicKeyRes ?? await getPublicKey()
        config._publicKeyRes = publicKeyRes
        config.headers = {
          ...config.headers,
          signAppId: ZF_NO,
          signType: 'SM4',
          signSerialNo: publicKeyRes.keySerialNo,
        }
      }

      return config
    },
    (error: any) => {
      return Promise.reject(error)
    },
  )

  axiosInstance.getAxios().interceptors.request.use(generateBodySignInterceptor())

  return axiosInstance
}
